import type { RepeatableField } from "@/features/dynamic-variables/repeatable/types";
import type { StructuredCsvResult } from "../structured-csv";

/**
 * CSV import/export for a repeatable group's rows — the same three-way flow the
 * Variables page offers for custom variables (upload, blank template, example
 * CSV), scoped to ONE group: the columns are the group's field keys.
 */

const csvCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const csvLine = (cells: string[]): string => cells.map(csvCell).join(",");

/** Header row only: one column per field key, in field order. */
export const createRepeatableCsvTemplate = (
  fields: RepeatableField[],
): string => `${csvLine(fields.map((f) => f.key))}\n`;

/** A plausible value for a field type, so the example reads like real data. */
export const exampleValueFor = (
  field: RepeatableField,
  row: number,
): string => {
  switch (field.type) {
    case "number":
      return row === 1 ? "1250.50" : "980";
    case "date":
      return row === 1 ? "2026-07-12" : "2026-09-01";
    case "color":
      return row === 1 ? "#610cff" : "#3fa2fe";
    case "image":
    case "media":
    case "image-video":
      return `https://example.com/${field.key}-${row}.png`;
    case "video":
      return `https://example.com/${field.key}-${row}.mp4`;
    case "iframe":
      return `https://example.com/embed/${row}`;
    case "select":
    case "multiselect":
      return row === 1 ? "Option A" : "Option B";
    default:
      return `${field.name} ${row}`;
  }
};

/** Header + two example rows. */
export const createRepeatableCsvExamples = (
  fields: RepeatableField[],
): string =>
  [
    csvLine(fields.map((f) => f.key)),
    csvLine(fields.map((f) => exampleValueFor(f, 1))),
    csvLine(fields.map((f) => exampleValueFor(f, 2))),
  ].join("\n");

export interface RepeatableCsvIssue {
  message: string;
}

export interface RepeatableCsvValidation {
  /** Rows normalised to the group's field keys (extra columns dropped, missing → ""). */
  rows: Record<string, string>[];
  issues: RepeatableCsvIssue[];
}

/**
 * Checks a parsed CSV against the group's fields. Columns must match the field
 * keys exactly (order-free); every mismatch is reported so the sender can fix
 * the file in one go. Rows are always returned normalised so the preview can
 * show them even when issues block the import.
 */
export const validateRepeatableCsv = (
  parsed: StructuredCsvResult,
  fields: RepeatableField[],
): RepeatableCsvValidation => {
  const expected = fields.map((f) => f.key);
  const expectedSet = new Set(expected);
  const headerSet = new Set(parsed.headers);
  const issues: RepeatableCsvIssue[] = [];

  const missing = expected.filter((key) => !headerSet.has(key));
  const extra = parsed.headers.filter((h) => h && !expectedSet.has(h));
  if (missing.length)
    issues.push({
      message: `Missing column${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}`,
    });
  if (extra.length)
    issues.push({
      message: `Unknown column${extra.length === 1 ? "" : "s"}: ${extra.join(", ")}`,
    });
  if (parsed.records.length === 0)
    issues.push({ message: "The file has no data rows below the header" });

  const rows = parsed.records.map((record) =>
    Object.fromEntries(expected.map((key) => [key, String(record[key] ?? "")])),
  );
  return { rows, issues };
};

/** Triggers a browser download of a CSV string. */
export const downloadCsv = (contents: string, filename: string) => {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  anchor.remove();
  // Revoke after the click has been dispatched: Safari/Firefox start the
  // download asynchronously and a synchronously revoked blob URL is empty.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};
